import { currencyFormatter } from "@/utils"
import { notifyChartFilterApplied } from "@/utils/filter_feedback"
import { TransactionJson } from "@/api"
import { BarList } from "@tremor/react"
import { useMemo, useState, useEffect } from "preact/compat"
import { BreakdownData } from "@/components/breakdown"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx"
import { Button } from "@/components/ui/button"
import { Calculator, X } from "lucide-react"
import { useSuperCoffee, useShowRedistribution } from "@/hooks/use_super_coffee"
import { Table } from "@tanstack/react-table"
import { AccountingState } from "@/transactions/state.tsx";
import { coding_amount } from "@/utils/transaction_amount"

const EXPENSE_ACCOUNT_COLORS = {
  "Uncoded": "red",
  "Sales Discounts": "blue",
  "Advertising": "green",
  "Freight": "orange",
  "Spoilage": "red",
  "Slotting": "purple",
  "Other": "gray",
  // Add more expense account colors as needed
}

// Color palette for accounts not in the predefined map
const COLOR_PALETTE = [
  "purple", "pink", "indigo", "cyan", "teal", "lime", "amber", "rose",
  "violet", "emerald", "sky", "fuchsia", "slate", "zinc", "neutral", "stone"
]

interface ExpenseAccountsBreakdownProps {
  table?: Table<TransactionJson>
}

/**
 * Dollar totals per expense account for the breakdown chart. Rows without splits count toward
 * "Uncoded" at their *coding target* — for an early-pay invoice that is only the discount taken
 * (`coding_amount`), not the full invoice face value, which previously inflated the Uncoded bar by
 * roughly the whole invoice.
 */
export function compute_expense_account_totals(
  transactions: Pick<TransactionJson, "kind" | "invoice_amount" | "discount_amount" | "splits">[]
): Record<string, number> {
  const accountTotals: Record<string, number> = {}

  for (let transaction of transactions) {
    if (!transaction.splits?.length) {
      // No splits means uncoded transaction
      const uncoded = coding_amount(transaction.kind, transaction.invoice_amount || 0, transaction.discount_amount)
      if (uncoded > 0) {
        accountTotals["Uncoded"] = (accountTotals["Uncoded"] || 0) + uncoded
      }
    } else {
      for (let split of transaction.splits) {
        const accountName = split.name
        accountTotals[accountName] = (accountTotals[accountName] || 0) + Math.abs(split.amount)
      }
    }
  }

  return accountTotals
}

export function ExpenseAccountsBreakdown({ table }: ExpenseAccountsBreakdownProps) {
  const [redistributedData, setRedistributedData] = useState<BreakdownData[]>([])
  const isSuperCoffee = useSuperCoffee()

  // Extract filtered transactions from table
  const transactions = useMemo(() => {
    return table?.getFilteredRowModel().rows.map(row => row.original) ?? []
  }, [table?.getFilteredRowModel().rows])

  let data = useMemo(() => {
    const accountTotals = compute_expense_account_totals(transactions)

    return Array.from(Object.entries(accountTotals))
      .map(([name, value], index) => ({
        key: name,
        name,
        value,
        color: EXPENSE_ACCOUNT_COLORS[name as keyof typeof EXPENSE_ACCOUNT_COLORS] || COLOR_PALETTE[index % COLOR_PALETTE.length],
      }))
      .sort((a, b) => b.value - a.value) // Sort by value descending
  }, [transactions])

  function redistributeUncodedAndMisc() {
    if (!data.length) return

    const uncodedItem = data.find(item => item.name === "Uncoded")
    const miscItem = data.find(item => item.name === "Misc")
    
    if (!uncodedItem && !miscItem) return

    const codedItems = data.filter(item => item.name !== "Uncoded" && item.name !== "Misc")
    if (!codedItems.length) return

    const totalCodedAmount = codedItems.reduce((sum, item) => sum + item.value, 0)
    const uncodedAmount = uncodedItem?.value || 0
    const miscAmount = miscItem?.value || 0
    const totalRedistributeAmount = uncodedAmount + miscAmount

    const redistributed = codedItems.map(item => ({
      ...item,
      value: item.value + (totalRedistributeAmount * (item.value / totalCodedAmount))
    }))

    setRedistributedData(redistributed)
  }

  function resetRedistribution() {
    setRedistributedData([])
  }

  // Reset redistribution when data changes
  useEffect(() => {
    setRedistributedData([])
  }, [data])

  if (!table) {
    return null
  }

  const displayData = redistributedData.length > 0 ? redistributedData : data
  const hasUncodedOrMisc = data.some(item => item.name === "Uncoded") || data.some(item => item.name === "Misc")
  const showRedistributeButton = isSuperCoffee && hasUncodedOrMisc

  return (
    <Card className="h-full">
      <CardHeader className="flex space-y-0 pb-2">
        <div className="flex items-center justify-between w-full">
          <CardTitle className="text-md font-medium">Expense Account Breakdown</CardTitle>
          {showRedistributeButton && (
            <div className="flex gap-2">
              {redistributedData.length > 0 ? (
                <Button
                  variant="outline"
                  onClick={resetRedistribution}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={redistributeUncodedAndMisc}
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Redistribute Uncoded & Misc
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <BarList
          data={displayData}
          className="mt-2"
          valueFormatter={currencyFormatter}
          onValueChange={(item: BreakdownData) => {
            AccountingState.set(state => {
              let columnFilters = [...state.columnFilters]
              // Remove existing reason_code and coded filters
              columnFilters = columnFilters.filter(f => f.id !== "reason_code" && f.id !== "coded")

              if (item?.name === "Uncoded") {
                // Filter for uncoded transactions
                columnFilters.push({ id: "coded", value: false })
              } else if (item?.name) {
                // Filter for specific reason code
                columnFilters.push({ id: "reason_code", value: item.name })
              }

              return { ...state, columnFilters }
            })
            if (item?.name === "Uncoded") {
              notifyChartFilterApplied("Showing uncoded transactions.")
            } else if (item?.name) {
              notifyChartFilterApplied(`Showing transactions coded to "${item.name}".`)
            }
          }}
        />
      </CardContent>
    </Card>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("compute_expense_account_totals", () => {
    it("counts an uncoded early-pay invoice at its discount, not its face value", () => {
      const totals = compute_expense_account_totals([
        { kind: "invoice", invoice_amount: 30363.61, discount_amount: 656.51, splits: [] },
      ] as any)
      expect(totals["Uncoded"]).toBeCloseTo(656.51)
    })

    it("skips invoices with no discount instead of adding a $0 Uncoded bar", () => {
      const totals = compute_expense_account_totals([
        { kind: "invoice", invoice_amount: 5000, discount_amount: null, splits: [] },
      ] as any)
      expect(totals["Uncoded"]).toBeUndefined()
    })

    it("counts uncoded deductions at their full amount", () => {
      const totals = compute_expense_account_totals([
        { kind: "deduction", invoice_amount: -450, discount_amount: null, splits: [] },
        { kind: "deduction", invoice_amount: -50, discount_amount: null, splits: [] },
      ] as any)
      expect(totals["Uncoded"]).toBe(500)
    })

    it("sums split magnitudes per account for coded rows", () => {
      const totals = compute_expense_account_totals([
        {
          kind: "deduction",
          invoice_amount: -100,
          discount_amount: null,
          splits: [
            { name: "Advertising", amount: 70 },
            { name: "Freight", amount: 30 },
          ],
        },
        { kind: "deduction", invoice_amount: -20, discount_amount: null, splits: [{ name: "Freight", amount: 20 }] },
      ] as any)
      expect(totals).toEqual({ Advertising: 70, Freight: 50 })
    })
  })
}
